import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import Button from './Button';

/**
 * Modal footer button configuration
 */
export interface FooterButton {
  /** Button text */
  text: string;
  /** Button variant/style */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Click handler */
  onClick: () => void;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Whether button shows loading state */
  isLoading?: boolean;
  /** Whether button is hidden */
  hidden?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button type for forms */
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Modal footer props interface
 */
export interface ModalFooterProps {
  /** Array of button configurations */
  buttons?: FooterButton[];
  /** Custom footer content (overrides buttons) */
  children?: React.ReactNode;
  /** Footer alignment */
  alignment?: 'left' | 'center' | 'right' | 'between' | 'around';
  /** Whether to show border top */
  showBorder?: boolean;
  /** Additional CSS classes for footer container */
  className?: string;
  /** Padding size */
  padding?: 'sm' | 'md' | 'lg';
  /** Whether footer is sticky at bottom */
  sticky?: boolean;
}

/**
 * ModalFooter - Reusable footer component for consistent button layouts
 * 
 * Features:
 * - Configurable button arrays with different variants
 * - Flexible alignment options
 * - Theme integration
 * - Loading and disabled states
 * - Custom content support
 * - Responsive design
 */
export const ModalFooter: React.FC<ModalFooterProps> = ({
  buttons = [],
  children,
  alignment = 'between',
  showBorder = true,
  className,
  padding = 'md',
  sticky = false,
}) => {
  const tc = useThemeClasses();

  // Filter out hidden buttons
  const visibleButtons = useMemo(() => 
    buttons.filter(button => !button.hidden), 
    [buttons]
  );

  // Memoize container classes
  const containerClasses = useMemo(() => {
    const baseClasses = [
      'flex items-center gap-2 sm:gap-3 w-full',
      tc.bg.surface,
    ];

    // Padding classes - responsive
    const paddingClasses = {
      sm: 'px-3 py-2 sm:px-4 sm:py-3',
      md: 'px-4 py-3 sm:px-6 sm:py-4',
      lg: 'px-6 py-4 sm:px-8 sm:py-6'
    };
    baseClasses.push(paddingClasses[padding]);

    // Alignment classes
    const alignmentClasses = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
      around: 'justify-around'
    };
    baseClasses.push(alignmentClasses[alignment]);

    // Border
    if (showBorder) {
      baseClasses.push('border-t', tc.border.primary);
    }

    // Sticky positioning
    if (sticky) {
      baseClasses.push('sticky bottom-0 z-10');
    }

    if (className) {
      baseClasses.push(className);
    }

    return cn(...baseClasses);
  }, [tc, padding, alignment, showBorder, sticky, className]);

  // Group buttons by alignment for 'between' layout
  const { leftButtons, rightButtons } = useMemo(() => {
    if (alignment !== 'between') {
      return { leftButtons: [], rightButtons: visibleButtons };
    }

    // For 'between' alignment, assume first buttons go left, primary/submit go right
    const left = visibleButtons.filter(btn => 
      btn.variant === 'secondary' || btn.variant === 'outline' || btn.variant === 'ghost'
    );
    const right = visibleButtons.filter(btn => 
      btn.variant === 'primary' || btn.type === 'submit' || !btn.variant
    );

    return { leftButtons: left, rightButtons: right };
  }, [alignment, visibleButtons]);

  // Render button group
  const renderButtonGroup = (buttonGroup: FooterButton[]) => (
    <div className="flex items-center gap-2 sm:gap-3">
      {buttonGroup.map((button, index) => (
        <Button
          key={index}
          variant={button.variant || 'primary'}
          type={button.type || 'button'}
          onClick={button.onClick}
          disabled={button.disabled}
          isLoading={button.isLoading}
          className={button.className}
        >
          {button.text}
        </Button>
      ))}
    </div>
  );

  // Custom content takes precedence
  if (children) {
    return (
      <footer className={containerClasses}>
        {children}
      </footer>
    );
  }

  // No buttons, render nothing
  if (visibleButtons.length === 0) {
    return null;
  }

  return (
    <footer className={containerClasses}>
      {alignment === 'between' ? (
        <>
          {leftButtons.length > 0 ? renderButtonGroup(leftButtons) : <div />}
          {rightButtons.length > 0 && renderButtonGroup(rightButtons)}
        </>
      ) : (
        renderButtonGroup(visibleButtons)
      )}
    </footer>
  );
};

/**
 * Helper functions for common button configurations
 */

/**
 * Create a standard back/next button configuration
 */
export const createStepButtons = (
  onBack?: () => void,
  onNext?: () => void,
  nextText: string = 'Continue',
  isLoading: boolean = false,
  nextDisabled: boolean = false
): FooterButton[] => {
  const buttons: FooterButton[] = [];

  if (onBack) {
    buttons.push({
      text: 'Back',
      variant: 'secondary',
      onClick: onBack,
      disabled: isLoading,
    });
  }

  if (onNext) {
    buttons.push({
      text: nextText,
      variant: 'primary',
      onClick: onNext,
      disabled: nextDisabled,
      isLoading,
      type: 'submit',
    });
  }

  return buttons;
};

/**
 * Create a cancel/confirm button configuration
 */
export const createConfirmButtons = (
  onCancel: () => void,
  onConfirm: () => void,
  confirmText: string = 'Confirm',
  cancelText: string = 'Cancel',
  isLoading: boolean = false,
  confirmDisabled: boolean = false
): FooterButton[] => [
  {
    text: cancelText,
    variant: 'outline',
    onClick: onCancel,
    disabled: isLoading,
  },
  {
    text: confirmText,
    variant: 'primary',
    onClick: onConfirm,
    disabled: confirmDisabled,
    isLoading,
    type: 'submit',
  },
];

/**
 * Create a payment button configuration
 */
export const createPaymentButtons = (
  onCancel: () => void,
  onPay: () => void,
  amount?: string,
  isProcessing: boolean = false,
  payDisabled: boolean = false
): FooterButton[] => [
  {
    text: 'Cancel',
    variant: 'outline',
    onClick: onCancel,
    disabled: isProcessing,
  },
  {
    text: amount ? `Pay ${amount}` : 'Process Payment',
    variant: 'primary',
    onClick: onPay,
    disabled: payDisabled,
    isLoading: isProcessing,
    type: 'submit',
  },
];

/**
 * Create a success/completion button configuration
 */
export const createSuccessButtons = (
  onComplete: () => void,
  buttonText: string = 'Complete'
): FooterButton[] => [
  {
    text: buttonText,
    variant: 'primary',
    onClick: onComplete,
  },
];

/**
 * Hook-based button creators with i18n support
 * Use these instead of create* functions for automatic translation
 */

export const useStepButtons = (
  onBack?: () => void,
  onNext?: () => void,
  nextText?: string,
  isLoading: boolean = false,
  nextDisabled: boolean = false
): FooterButton[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    const buttons: FooterButton[] = [];

    if (onBack) {
      buttons.push({
        text: t('buttons.back'),
        variant: 'secondary',
        onClick: onBack,
        disabled: isLoading,
      });
    }

    if (onNext) {
      buttons.push({
        text: nextText || t('buttons.continue'),
        variant: 'primary',
        onClick: onNext,
        disabled: nextDisabled,
        isLoading,
        type: 'submit',
      });
    }

    return buttons;
  }, [t, onBack, onNext, nextText, isLoading, nextDisabled]);
};

export const useConfirmButtons = (
  onCancel: () => void,
  onConfirm: () => void,
  confirmText?: string,
  cancelText?: string,
  isLoading: boolean = false,
  confirmDisabled: boolean = false
): FooterButton[] => {
  const { t } = useTranslation();

  return useMemo(() => [
    {
      text: cancelText || t('buttons.cancel'),
      variant: 'outline' as const,
      onClick: onCancel,
      disabled: isLoading,
    },
    {
      text: confirmText || t('buttons.confirm'),
      variant: 'primary' as const,
      onClick: onConfirm,
      disabled: confirmDisabled,
      isLoading,
      type: 'submit' as const,
    },
  ], [t, onCancel, onConfirm, confirmText, cancelText, isLoading, confirmDisabled]);
};